import React, { useState, useRef, useEffect } from "react";
import { Hexagon, LogOut, Settings, X, User, Mail, Lock, Phone } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Avatar, AvatarFallback } from "../ui/avatar";

const ROLE_LABEL = {
  FleetAdmin: "Fleet Admin",
  ReleaseManager: "Release Manager",
  SupportEngineer: "Support Engineer",
};

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

function SettingsModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-slate-900">Account Settings</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Full Name</label>
            <div className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-2.5 bg-slate-50">
              <User className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-sm text-slate-700">Admin User</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
            <div className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-2.5 bg-slate-50">
              <Mail className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-sm text-slate-700">admin@tetramed.com</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Password</label>
            <div className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-2.5 bg-slate-50">
              <Lock className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-sm text-slate-700 tracking-widest">••••••••</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Phone Number</label>
            <div className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-2.5 bg-slate-50">
              <Phone className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-sm text-slate-700">+1 (555) 000-0000</span>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-5 text-center">Edit functionality coming soon.</p>
      </div>
    </div>
  );
}

export function Header() {
  const { user, role, logout } = useAuth();
  const [showSettings, setShowSettings] = useState(false);

  return (
    <>
      <header className="h-16 bg-[hsl(196,64%,24%)] flex items-center justify-between px-6 shrink-0">
        {/* Brand */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-teal-700 text-teal-300">
            <Hexagon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-none">
              TETRA MICRO INFUSION SYSTEM
            </p>
            <p className="text-teal-300 text-[11px] mt-0.5 leading-none">Fleet Management Portal</p>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-3">
          {/* Role badge */}
          <div className="flex items-center gap-1.5 bg-teal-700/50 border border-teal-600 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
            <span className="text-white text-xs font-medium">{ROLE_LABEL[role] || role}</span>
          </div>

          {/* Avatar */}
          <Avatar className="h-8 w-8 cursor-pointer">
            <AvatarFallback className="bg-teal-600 text-white text-xs font-semibold">
              {initials(user?.name)}
            </AvatarFallback>
          </Avatar>

          {/* Settings */}
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 text-teal-100 hover:text-white text-xs border border-teal-600 hover:border-teal-400 rounded-md px-3 py-1.5 transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            Settings
          </button>

          {/* Sign out */}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-teal-100 hover:text-white text-xs border border-teal-600 hover:border-teal-400 rounded-md px-3 py-1.5 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign Out
          </button>
        </div>
      </header>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </>
  );
}
